import React, { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { Auth, API } from "aws-amplify";
import { useDispatch } from "react-redux";
import {
  createAccountAction,
  getAccountByEmail,
  getAccountDetails,
} from "Sdk/Actions";
import { login } from "Store/reducers/Auth/AuthSlice";
import Logo from "@/Assets/Logo/logo.png";
import AuthImage from "@/Assets/auth-image.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";

function sanitizeUsername(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    // Keeps letters, numbers, spaces, and hyphens
    .replace(/[^a-zA-Z0-9-]/g, "-")
    .replace(/-+/g, "-");
}

function normalizeUsername(value) {
  return sanitizeUsername(value).replace(/^-|-$/g, "");
}

function suggestUsernameFromEmail(email) {
  const localPart = String(email || "").split("@")[0] || "";
  return sanitizeUsername(localPart);
}

export default function Onboarding() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState("");
  const [checkingTimeoutId, setCheckingTimeoutId] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [username, setUsername] = useState("");

  const email = useMemo(() => authUser?.attributes?.email || "", [authUser]);

  const checkAvailability = useCallback(async (username) => {
    if (!username) {
      setUsernameStatus("");
      return;
    }

    const validUserNameRegex = /^[a-z0-9-]+$/;
    if (!validUserNameRegex.test(username)) {
      setUsernameStatus("invalid");
      return;
    }

    setIsChecking(true);
    setUsernameStatus("checking");

    try {
      const data = await API.get("REST", `/company?name=${username}&check=true`);
      if (data?.isUserNameAvailable) {
        setUsernameStatus("taken");
      } else {
        setUsernameStatus("available");
      }
    } catch (error) {
      console.error("Username check failed:", error);
      setUsernameStatus("error");
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(async (user) => {
        setAuthUser(user);

        const attrs = user?.attributes || {};
        const preferred = attrs?.preferred_username;
        const profileDone = attrs?.profile === "true";
        if (preferred && profileDone) {
          dispatch(
            login({
              token: user?.signInUserSession?.accessToken?.jwtToken,
            }),
          );
          await getAccountDetails(preferred);
          navigate("/dashboard", { replace: true });
          return;
        }

        const suggestedUsername = suggestUsernameFromEmail(attrs?.email);
        if (suggestedUsername) {
          setUsername(suggestedUsername);
          checkAvailability(normalizeUsername(suggestedUsername));
        }
      })
      .catch(() => {
        navigate("/login", { replace: true });
      });
  }, [navigate, checkAvailability, dispatch]);

  useEffect(() => {
    return () => {
      if (checkingTimeoutId) clearTimeout(checkingTimeoutId);
    };
  }, [checkingTimeoutId]);

  const onUsernameChange = (e) => {
    const value = sanitizeUsername(e?.target?.value);
    setUsername(value);

    if (checkingTimeoutId) clearTimeout(checkingTimeoutId);

    const timeout = setTimeout(() => {
      checkAvailability(normalizeUsername(value));
    }, 450);
    setCheckingTimeoutId(timeout);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!authUser) return;

    const finalUsername = normalizeUsername(username);
    if (!finalUsername) {
      message.error("Please enter a valid username.");
      return;
    }

    if (usernameStatus !== "available") {
      message.error("Please choose an available username.");
      return;
    }

    setIsLoading(true);
    try {
      const attrs = authUser?.attributes || {};
      const tokenPayload =
        authUser?.signInUserSession?.idToken?.payload ||
        authUser?.signInUserSession?.accessToken?.payload ||
        {};

      const resolvedEmail = attrs?.email || tokenPayload?.email || "";
      const existingByEmail = await getAccountByEmail(resolvedEmail);

      if (
        existingByEmail?.username &&
        existingByEmail.username !== finalUsername
      ) {
        message.error(
          "This email is already linked to an existing profile. Please log in with that account.",
        );
        navigate("/login", { replace: true });
        return;
      }

      const nextBillingAt = new Date(
        Date.now() + 7 * 24 * 60 * 60 * 1000,
      ).toISOString();

      const userData = {
        username: finalUsername,
        email: resolvedEmail || finalUsername,
        name:
          attrs?.name ||
          String(finalUsername[0]).toUpperCase() + String(finalUsername).slice(1),
        nextBillingAt,
        userType: "trial",
        paidWidgets: ["review-generator", "seo"],
      };

      if (!existingByEmail?.username) {
        await createAccountAction(userData);
      }

      await Auth.updateUserAttributes(authUser, {
        preferred_username: finalUsername,
        profile: "true",
      });

      dispatch(
        login({
          token: authUser?.signInUserSession?.accessToken?.jwtToken,
        }),
      );

      await getAccountDetails(finalUsername);
      navigate("/dashboard?new_signup=true", { replace: true });
    } catch (err) {
      const errMsg = err?.message || "Something went wrong.";
      if (
        err?.code === "ConditionalCheckFailedException" ||
        errMsg.toLowerCase().includes("exists")
      ) {
        message.error("This username is already taken. Please choose another.");
      } else {
        message.error(errMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const statusText =
    usernameStatus === "checking"
      ? "Checking availability..."
      : usernameStatus === "available"
      ? "Username is available"
      : usernameStatus === "taken"
      ? "This username is already taken"
      : usernameStatus === "invalid"
      ? "Use lowercase letters, numbers, and hyphens"
      : usernameStatus === "error"
      ? "Could not validate username right now"
      : "";

  const canSubmit = usernameStatus === "available" && !isChecking && !isLoading;

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Link
            to="/"
            className="flex items-center gap-2 font-semibold"
            aria-label="Inprofile home"
          >
            <img src={Logo} alt="Inprofile" className="h-8 w-8" />
            Inprofile
          </Link>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm">
            <h1 className="text-xl font-semibold text-gray-900 mb-1">
              Choose your username
            </h1>
            <p className="text-gray-600 mb-6">
              This will be your public profile URL and unique identity.
            </p>
            {email ? (
              <p className="text-sm text-gray-500 mb-4">Signed in as {email}</p>
            ) : null}
            <form onSubmit={handleSubmit}>
              <label htmlFor="username" className="text-sm font-medium text-gray-900">
                Username
              </label>
              <Input
                id="username"
                name="username"
                placeholder="your-business-name"
                className="mt-2 mb-4 h-11"
                autoComplete="username"
                value={username}
                onChange={onUsernameChange}
              />
              {statusText ? (
                <div
                  className={`text-sm mb-4 ${
                    usernameStatus === "available"
                      ? "text-green-600"
                      : usernameStatus === "checking"
                      ? "text-blue-600"
                      : "text-red-600"
                  }`}
                >
                  {statusText}
                </div>
              ) : null}
              <Button
                type="submit"
                disabled={!canSubmit}
                className="w-full h-11 font-semibold"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Please wait...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
      <div className="bg-[#f2f5f8] relative hidden lg:block">
        <img
          src={AuthImage}
          alt=""
          className="absolute inset-0 h-full w-full object-contain dark:brightness-[0.2] dark:grayscale"
        />
      </div>
    </div>
  );
}
